import React from 'react';
import type { MarMedicationResponse } from '../../../services/api';
import AddAdministrationRecordButton from './AddAdministrationRecordButton';
import MarEntryActions from './MarEntryActions';
import { formatTimeOfDay } from '../../../utils/dateUtils';

interface AdministrationRecordsTableProps {
  entries: MarMedicationResponse[];
  /** Whether an administration can be recorded right now. */
  canRecord: boolean;
  /** Explanation shown when recording is blocked. */
  blockedReason: string;
  onAdd: () => void;
  onEdit: (entry: MarMedicationResponse) => void;
  onDelete: (entry: MarMedicationResponse) => void;
}

/** Expanded panel listing a medication's administration records for the visit. */
const AdministrationRecordsTable: React.FC<AdministrationRecordsTableProps> = ({
  entries,
  canRecord,
  blockedReason,
  onAdd,
  onEdit,
  onDelete,
}) => {
  return (
    <div className='p-3'>
      <div className='overflow-x-auto rounded-md border bg-background'>
        <table className='w-full text-sm'>
          <thead>
            <tr className='border-b bg-muted/40 text-xs text-muted-foreground'>
              <th className='w-32 px-3 py-2 text-left font-medium'>
                Time Administered
              </th>
              <th className='px-3 py-2 text-left font-medium'>PRN Reason</th>
              <th className='px-3 py-2 text-left font-medium'>
                Patient Response
              </th>
              <th className='w-20 px-3 py-2 text-right font-medium'>Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(entry => (
              <tr key={entry.id} className='border-b align-top last:border-0'>
                <td className='whitespace-nowrap px-3 py-2 font-medium'>
                  {formatTimeOfDay(entry.time_administered) || 'Time not set'}
                </td>
                <td className='px-3 py-2 text-muted-foreground'>
                  {entry.prn_reason || '—'}
                </td>
                <td className='px-3 py-2 text-muted-foreground'>
                  {entry.patient_response || '—'}
                </td>
                <td className='px-3 py-2'>
                  <div className='flex items-center justify-end gap-1'>
                    <MarEntryActions
                      entry={entry}
                      canEdit={canRecord}
                      disabledReason={blockedReason}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      iconClassName='h-3.5 w-3.5'
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className='border-t px-3 py-2'>
          <AddAdministrationRecordButton
            canRecord={canRecord}
            blockedReason={blockedReason}
            onAdd={onAdd}
          />
        </div>
      </div>
    </div>
  );
};

export default AdministrationRecordsTable;
